
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PlusIcon, FileTextIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ProcurementDocumentsProps {
  procurementId: string;
}

export function ProcurementDocuments({ procurementId }: ProcurementDocumentsProps) {
  const isMobile = useIsMobile();
  const [documents, setDocuments] = useState<{ id: string; name: string }[]>([]);
  
  // This is a placeholder function for document upload
  const handleUploadClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.png';
    fileInput.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        // In a real implementation, this would upload the file to storage
        // For now, we'll just add it to our local state
        setDocuments([...documents, { 
          id: Date.now().toString(),
          name: file.name
        }]);
      }
    };
    fileInput.click();
  };
  
  const handleDeleteDocument = (id: string) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Documents</h3>
          <Button 
            onClick={handleUploadClick} 
            variant="outline" 
            size={isMobile ? "sm" : "default"}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {isMobile ? "Upload" : "Upload Document"}
          </Button>
        </div>
        
        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 border border-dashed rounded-md">
            <FileTextIcon className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-center">
              No documents uploaded yet. Upload documents related to this procurement.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {documents.map(doc => (
              <div 
                key={doc.id}
                className="flex justify-between items-center p-3 border rounded-md hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center space-x-2 overflow-hidden">
                  <FileTextIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{doc.name}</span>
                </div>
                <div className="flex space-x-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <DownloadIcon className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteDocument(doc.id)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
